/* eslint-disable react/prop-types */
import React from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { Select as ChakraReactSelect } from "chakra-react-select";

import {
  agencyContactFromDraft,
  employerContactFromDraft,
} from "../helpers/jobContactFields";
import { editFieldLabel, formatDateInputValue } from "../helpers/jobCreateFlowHelpers";
import {
  COVER_IMAGES,
  EMPLOYMENT_LABELS,
  PAY_CURRENCY_OPTIONS,
  PAY_FREQUENCY_OPTIONS,
} from "./JobPreviewCard";
import JobOwnerSelect, { JOB_OWNER_HELP_TEXT } from "./JobOwnerSelect";

const EMPLOYMENT_TYPE_OPTIONS = Object.entries(EMPLOYMENT_LABELS).map(([value, label]) => ({
  value,
  label,
}));

function ClientOrganizationSelect({ clientOrgs, value, onChange, placeholder = "Select department" }) {
  const options = React.useMemo(
    () => clientOrgs.map((c) => ({ value: c.id, label: c.name })),
    [clientOrgs]
  );
  const selected = options.find((o) => o.value === value) || null;

  return (
    <ChakraReactSelect
      options={options}
      value={selected}
      onChange={(opt) => onChange(opt?.value || null)}
      placeholder={placeholder}
      colorScheme="blue"
      variant="outlineDark"
      isClearable
    />
  );
}

function EmploymentTypeSelect({ value, onChange }) {
  const selected = EMPLOYMENT_TYPE_OPTIONS.find((o) => o.value === value) || null;

  return (
    <ChakraReactSelect
      options={EMPLOYMENT_TYPE_OPTIONS}
      value={selected}
      onChange={(opt) => onChange(opt?.value || null)}
      placeholder="Select employment type"
      colorScheme="blue"
      variant="outlineDark"
      isClearable
    />
  );
}

function StartDateInput({ startDate, isAsap, onChange }) {
  return (
    <FormControl>
      <FormLabel fontSize="md" color="white">
        Start Date
      </FormLabel>
      <HStack spacing={4} align="flex-start">
        <Checkbox
          isChecked={isAsap || false}
          colorScheme="whiteAlpha"
          color="white"
          pt="8px"
          onChange={(e) => {
            const checked = e.target.checked;
            onChange({
              isAsap: checked,
              startDate: checked ? null : startDate,
            });
          }}
        >
          <Text color="white">ASAP</Text>
        </Checkbox>
        <Box flex="1">
          <Input
            type="date"
            variant="outlineDark"
            style={{ colorScheme: "dark" }}
            value={formatDateInputValue(startDate)}
            disabled={isAsap}
            onChange={(e) =>
              onChange({
                isAsap: false,
                startDate: e.target.value
                  ? new Date(`${e.target.value}T12:00:00`).toISOString()
                  : null,
              })
            }
          />
        </Box>
      </HStack>
    </FormControl>
  );
}

function EditModal({ field, draft, agencyType, clientOrgs, vessels, workLocationOptions, onClose, onSave }) {
  const [local, setLocal] = React.useState({});
  React.useEffect(() => {
    if (!field || !draft) return;
    setLocal({ ...draft });
  }, [field, draft]);

  if (!field) return null;

  const vesselOptions = (vessels || []).map((v) => ({ value: v.id, label: v.name }));

  const handlePrimaryContactChange = (key, value) => {
    const map = {
      name: "agencyContactName",
      email: "agencyContactEmail",
      phone: "agencyContactPhone",
    };
    setLocal((prev) => ({ ...prev, [map[key]]: value }));
  };

  const handleEmployerContactChange = (key, value) => {
    const map = {
      name: "employerContactName",
      email: "employerContactEmail",
      phone: "employerContactPhone",
    };
    setLocal((prev) => ({ ...prev, [map[key]]: value }));
  };

  const agencyContact = agencyContactFromDraft(local);
  const employerContact = employerContactFromDraft(local);

  return (
    <Modal
      isOpen={!!field}
      onClose={onClose}
      size={field === "coverImage" ? "xl" : "md"}
      variant="darkMode"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit {editFieldLabel(field, agencyType)}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {field === "category" && (
            <Input variant="outlineDark" value={local.category || ""} onChange={(e) => setLocal({ ...local, category: e.target.value })} />
          )}
          {field === "client" && (
            <ClientOrganizationSelect
              clientOrgs={clientOrgs}
              value={local.clientOrganizationId}
              onChange={(clientOrganizationId) =>
                setLocal({ ...local, clientOrganizationId })
              }
            />
          )}
          {field === "owner" && (
            <Stack spacing={2}>
              <FormControl>
                <FormLabel htmlFor="jobOwner">Owner</FormLabel>
                <JobOwnerSelect
                  inputId="jobOwner"
                  value={local.owner || null}
                  onChange={(owner) => setLocal({ ...local, owner })}
                />
              </FormControl>
              <Text fontSize="sm" color="gray.400">
                {JOB_OWNER_HELP_TEXT}
              </Text>
            </Stack>
          )}
          {field === "location" && (
            <Input
              variant="outlineDark"
              placeholder="e.g. Aberdeen, UK"
              value={local.location || ""}
              onChange={(e) => setLocal({ ...local, location: e.target.value })}
            />
          )}
          {field === "jobLocations" && (
            <ChakraReactSelect
              isMulti
              options={workLocationOptions}
              value={workLocationOptions.filter((o) =>
                (local.candidateWorkLocationIds || []).includes(o.value)
              )}
              onChange={(opts) =>
                setLocal({
                  ...local,
                  candidateWorkLocationIds: (opts || []).map((o) => o.value),
                })
              }
              placeholder="Select regions"
              variant="outlineDark"
              colorScheme="blue"
            />
          )}
          {field === "duration" && (
            <Input
              variant="outlineDark"
              placeholder="e.g. 6 months"
              value={local.duration || ""}
              onChange={(e) => setLocal({ ...local, duration: e.target.value })}
            />
          )}
          {field === "employmentType" && (
            <EmploymentTypeSelect
              value={local.employmentType}
              onChange={(employmentType) => setLocal({ ...local, employmentType })}
            />
          )}
          {field === "startDate" && (
            <StartDateInput
              startDate={local.startDate}
              isAsap={local.isAsap}
              onChange={({ startDate, isAsap }) => setLocal({ ...local, startDate, isAsap })}
            />
          )}
          {field === "numOpenings" && (
            <Input type="number" min={1} variant="outlineDark" value={local.numOpenings || 1} onChange={(e) => setLocal({ ...local, numOpenings: parseInt(e.target.value, 10) || 1 })} />
          )}
          {field === "requirements" && (
            <Textarea variant="outlineDark" minH="160px" value={local.requirementsDescription || ""} onChange={(e) => setLocal({ ...local, requirementsDescription: e.target.value })} />
          )}
          {field === "responsibilities" && (
            <Textarea variant="outlineDark" minH="160px" value={local.responsibilitiesDescription || ""} onChange={(e) => setLocal({ ...local, responsibilitiesDescription: e.target.value })} />
          )}
          {field === "pay" && (
            <Stack spacing={3}>
              <Box>
                <Text fontSize="sm" mb={1}>Currency</Text>
                <ChakraReactSelect
                  options={PAY_CURRENCY_OPTIONS}
                  value={PAY_CURRENCY_OPTIONS.find((o) => o.value === local.payCurrency) || null}
                  onChange={(opt) => setLocal({ ...local, payCurrency: opt?.value || null })}
                  variant="outlineDark"
                  colorScheme="blue"
                  isClearable
                />
              </Box>
              <Box>
                <Text fontSize="sm" mb={1}>Rate</Text>
                <NumberInput
                  value={local.payRateValue ?? ""}
                  onChange={(v) => setLocal({ ...local, payRateValue: v === "" ? null : parseFloat(v) })}
                  min={0}
                >
                  <NumberInputField variant="outlineDark" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </Box>
              <Box>
                <Text fontSize="sm" mb={1}>Frequency</Text>
                <ChakraReactSelect
                  options={PAY_FREQUENCY_OPTIONS}
                  value={PAY_FREQUENCY_OPTIONS.find((o) => o.value === local.payFrequency) || null}
                  onChange={(opt) => setLocal({ ...local, payFrequency: opt?.value || null })}
                  variant="outlineDark"
                  colorScheme="blue"
                  isClearable
                />
              </Box>
            </Stack>
          )}
          {field === "vessel" && (
            <ChakraReactSelect
              options={vesselOptions}
              value={vesselOptions.find((o) => o.value === local.vesselId) || null}
              onChange={(opt) => setLocal({ ...local, vesselId: opt?.value || null })}
              placeholder="Select vessel"
              variant="outlineDark"
              colorScheme="blue"
              isClearable
            />
          )}
          {field === "employerContact" && (
            <Stack spacing={3}>
              <Box>
                <Text fontSize="sm" mb={1}>Name</Text>
                <Input
                  variant="outlineDark"
                  value={employerContact.name}
                  placeholder="Name"
                  onChange={(e) => handleEmployerContactChange("name", e.target.value || null)}
                />
              </Box>
              <Box>
                <Text fontSize="sm" mb={1}>Email</Text>
                <Input
                  variant="outlineDark"
                  type="email"
                  value={employerContact.email}
                  onChange={(e) => handleEmployerContactChange("email", e.target.value)}
                />
              </Box>
              <Box>
                <Text fontSize="sm" mb={1}>Phone</Text>
                <Input
                  variant="outlineDark"
                  value={employerContact.phone}
                  onChange={(e) => handleEmployerContactChange("phone", e.target.value)}
                />
              </Box>
            </Stack>
          )}
          {field === "primaryContact" && (
            <Stack spacing={3}>
              <Box>
                <Text fontSize="sm" mb={1}>Name</Text>
                <Input
                  variant="outlineDark"
                  value={agencyContact.name}
                  placeholder="Name"
                  onChange={(e) => handlePrimaryContactChange("name", e.target.value || null)}
                />
              </Box>
              <Box>
                <Text fontSize="sm" mb={1}>Email</Text>
                <Input
                  variant="outlineDark"
                  type="email"
                  value={agencyContact.email}
                  onChange={(e) => handlePrimaryContactChange("email", e.target.value)}
                />
              </Box>
              <Box>
                <Text fontSize="sm" mb={1}>Phone</Text>
                <Input
                  variant="outlineDark"
                  value={agencyContact.phone}
                  onChange={(e) => handlePrimaryContactChange("phone", e.target.value)}
                />
              </Box>
            </Stack>
          )}
          {field === "coverImage" && (
            <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
              {COVER_IMAGES.map((src, idx) => {
                // An uploaded cover (coverImageUrl) is "selected" until the user
                // picks a predefined one, which replaces it.
                const selected = !local.coverImageUrl && local.coverImageIndex === idx;
                return (
                  <Box
                    key={src}
                    as="button"
                    onClick={() => setLocal({ ...local, coverImageIndex: idx, coverImageUrl: null })}
                    borderWidth={selected ? "3px" : "1px"}
                    borderColor={selected ? "blue.500" : "gray.200"}
                    borderRadius="md"
                    overflow="hidden"
                    cursor="pointer"
                  >
                    <Image src={src} alt={`Cover ${idx + 1}`} w="100%" h="100px" objectFit="cover" />
                  </Box>
                );
              })}
            </SimpleGrid>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button colorScheme="blue" onClick={() => onSave(local)}>Save</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export { ClientOrganizationSelect, EditModal, EmploymentTypeSelect, StartDateInput };
export default EditModal;
